import { useContractMandateGroups } from "@rentiohq/shared-frontend/dist/hooks/useContractMandateGroups";
import * as authSelectors from "@rentiohq/shared-frontend/dist/redux/auth/auth.selectors";
import * as paymentApi from "@rentiohq/shared-frontend/dist/redux/payment/payment.api";
import { getUpdateMandateErrorMessage } from "@rentiohq/shared-frontend/dist/redux/payment/payment.utils";
import { IPartialRootState } from "@rentiohq/shared-frontend/dist/redux/types";
import * as bankAccountActions from "@rentiohq/shared-frontend/dist/reduxV2/bankAccount/bankAccount.actions";
import * as bankAccountApi from "@rentiohq/shared-frontend/dist/reduxV2/bankAccount/bankAccount.api";
import * as bankAccountSelectors from "@rentiohq/shared-frontend/dist/reduxV2/bankAccount/bankAccount.selectors";
import * as paymentRequestApi from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.api";
import { IContract } from "@rentiohq/shared-frontend/dist/types/contract.types";
import {
  IContractMandateGroup,
  NO_CONTINUOUS_VALUE,
} from "@rentiohq/shared-frontend/dist/utils/contractMandateGroup.utils";
import { getMessageForError } from "@rentiohq/shared-frontend/dist/utils/error.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Box,
  Button,
  Divider,
  Link,
  MainCard,
  RadioButtonsGroup,
  SpinningLoader,
  Stack,
  Typography,
} from "@rentiohq/web-shared-next/dist/ui-components";
import { enqueueSnackbar } from "@rentiohq/web-shared-next/dist/utils/snackbar";
import { EButtonVariants } from "@rentiohq/web-shared-next/dist/utils/types/button";
import queryString from "query-string";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DIRECT_DEBITS_ROUTE,
  isTrustedMandateRedirectUrl,
} from "../../Payments.utils";
import { ContractDurationLink } from "../ContractDurationLink";
import { ManageDirectDebitsStyles } from "./ManageDirectDebits.styles";

interface IRadioOption {
  label: React.ReactNode;
  value: number;
}

interface IContractMandateGroupBlockProps {
  group: IContractMandateGroup;
  contract?: IContract;
  selectedIbanId: number;
  radioOptions: IRadioOption[];
  isSaving: boolean;
  hasUnsavedChange: boolean;
  onChangeSelectedIbanId: (id: number) => void;
  onSave: () => void;
}

const ContractMandateGroupBlock = ({
  group,
  contract,
  selectedIbanId,
  radioOptions,
  isSaving,
  hasUnsavedChange,
  onChangeSelectedIbanId,
  onSave,
}: IContractMandateGroupBlockProps) => {
  const sx = ManageDirectDebitsStyles();

  return (
    <Stack direction="column" gap={2}>
      {contract && (
        <Stack gap={1} flexDirection="row" alignItems="baseline">
          <Box component="span" sx={sx.content.contractLabel}>
            {getLocalizedText("system.contract")}:
          </Box>
          <ContractDurationLink contract={contract} />
        </Stack>
      )}
      <RadioButtonsGroup
        id={`direct-debit-iban-${group.contractId}`}
        label=""
        value={selectedIbanId}
        options={radioOptions}
        onChange={id => onChangeSelectedIbanId(Number(id))}
        getOptionSx={sx.content.option}
        radioProps={{
          sx: sx.content.radio,
          icon: <Box sx={sx.content.radioIcon} />,
          checkedIcon: <Box sx={sx.content.radioCheckedIcon} />,
        }}
      />
      <Stack direction="row" flexWrap="wrap" gap={1.5} alignItems="center">
        <Button
          variant={EButtonVariants.Contained}
          onClick={onSave}
          disabled={isSaving}
          sx={sx.content.saveButton}
        >
          {getLocalizedText("system.save")}
        </Button>
        {hasUnsavedChange && (
          <Typography variant="subtitle2">
            {getLocalizedText("tenant.payments.direct_debit.unsaved_change")}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export const ManageDirectDebits = () => {
  const sx = ManageDirectDebitsStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedIbanIdByContract, setSelectedIbanIdByContract] =
    React.useState<Record<string, number>>({});
  const [savingContractId, setSavingContractId] = React.useState<
    string | undefined
  >();

  const user = useSelector((state: IPartialRootState) =>
    authSelectors.getUser(state),
  );

  const ibans = useSelector((state: IPartialRootState) =>
    bankAccountSelectors.getContactBankAccounts(state, {
      contactId: user?.personalContactId,
    }),
  );

  const isFetchingIbans = useSelector((state: IPartialRootState) =>
    bankAccountSelectors.isFetchingContactBankAccounts(state, {
      contactId: user?.personalContactId,
    }),
  );

  const ibansFetchError = useSelector((state: IPartialRootState) =>
    bankAccountSelectors.contactBankAccountsFetchError(state, {
      contactId: user?.personalContactId,
    }),
  );

  // Without this the failure was completely silent: the account list stayed
  // empty, so the screen showed "no continuous direct debit" as the only option
  // and read like the tenant's direct debit had been cancelled.
  React.useEffect(() => {
    if (!ibansFetchError) {
      return;
    }

    enqueueSnackbar({
      message: getMessageForError(ibansFetchError),
      variant: "error",
    });
  }, [ibansFetchError]);

  const {
    contractGroups,
    contractsById,
    isFetchingContractGroups,
    isResolvingContracts,
    contractGroupsFetchError,
  } = useContractMandateGroups(user);

  // A failed load leaves no contract blocks at all, which reads like the tenant
  // has no direct debit to manage.
  React.useEffect(() => {
    if (!contractGroupsFetchError) {
      return;
    }

    enqueueSnackbar({
      message: getMessageForError(contractGroupsFetchError),
      variant: "error",
    });
  }, [contractGroupsFetchError]);

  const contractGroupsRef = React.useRef(contractGroups);
  React.useEffect(() => {
    contractGroupsRef.current = contractGroups;
  }, [contractGroups]);

  React.useEffect(() => {
    if (user?.personalContactId) {
      dispatch(
        bankAccountActions.getContactBankAccountsStart.getAction({
          contactId: user.personalContactId,
        }),
      );
    }
  }, [user?.personalContactId]);

  React.useEffect(() => {
    setSelectedIbanIdByContract(current => {
      const missingGroups = contractGroups.filter(
        group => current[group.contractId] === undefined,
      );

      if (missingGroups.length === 0) {
        return current;
      }

      const next = { ...current };
      missingGroups.forEach(group => {
        next[group.contractId] = group.activeBankAccountId;
      });
      return next;
    });
  }, [contractGroups]);

  // A failed save leaves the old mandate in place, so the radio group must show
  // that one again rather than the account the tenant had just picked.
  const revertSelectedIbanId = (group: IContractMandateGroup) => {
    setSelectedIbanIdByContract(current => ({
      ...current,
      [group.contractId]: group.activeBankAccountId,
    }));
  };

  // Actually switches the recurring mandate for a contract's paymentGroup -
  // only safe to call once the selected bank account already has an approved
  // MangoPay mandate (or the tenant opted out of continuous DD entirely).
  const submitMandateUpdate = async (
    group: IContractMandateGroup,
    bankAccountId: number | null,
  ) => {
    setSavingContractId(group.contractId);

    try {
      await paymentApi.updateMandate(group.paymentOrderId, {
        bankAccountId,
        recurring: !!bankAccountId,
      });
      navigate("/payments");
    } catch (unknownError) {
      revertSelectedIbanId(group);
      enqueueSnackbar({
        message: getUpdateMandateErrorMessage(unknownError),
        variant: "error",
      });
    } finally {
      setSavingContractId(undefined);
    }
  };

  // --- Mandate approval for a newly-added bank account ---
  // Flow: user adds a bank account -> we immediately request a MangoPay
  // mandate approval and redirect there -> MangoPay redirects back here with
  // the same `ibanId` (plus `autoSubmit=1`, marking the request as already
  // made). Once the approval lands, we only pre-select that account for the
  // user to review - the mandate switch itself always still requires the
  // user to press Save.
  // requestMandateApproval (via getMandate) already retries internally on the
  // "bank account not yet valid" error (see retry.utils.ts) - only attempt it
  // once here, don't stack a second retry loop on top of that one.
  const [isPendingMandateResolved, setIsPendingMandateResolved] =
    React.useState(false);
  const pendingMandateIntervalRef = React.useRef<NodeJS.Timeout>();
  const hasRequestedMandateRef = React.useRef(false);

  const { ibanId: pendingMandateIbanIdRaw, autoSubmit } = queryString.parse(
    location.search,
  ) as {
    ibanId?: string;
    autoSubmit?: string;
  };
  const pendingMandateIbanId = pendingMandateIbanIdRaw
    ? parseInt(pendingMandateIbanIdRaw, 10)
    : undefined;
  const hasAlreadyRequestedMandate = autoSubmit === "1";

  const getMandateCallbackUrl = (ibanId: number) => {
    const base = `${window.location.origin}${window.location.pathname}`;
    return queryString.stringifyUrl({
      url: base,
      query: { ibanId: `${ibanId}`, autoSubmit: "1" },
    });
  };

  const fetchIban = async (id: number) => {
    if (!user) {
      return;
    }
    const { data: freshIbans } = await bankAccountApi.getBankAccountsForContact(
      user.personalContactId,
    );
    return freshIbans.find(iban => iban.id === id);
  };

  const requestMandateApproval = async (ibanId: number) => {
    const { data: mandateRequest } = await paymentRequestApi.getMandate(
      ibanId,
      getMandateCallbackUrl(ibanId),
    );

    if (!isTrustedMandateRedirectUrl(mandateRequest.RedirectUrl)) {
      throw new Error("Invalid mandate redirect URL");
    }

    window.open(mandateRequest.RedirectUrl, "_self");
  };

  const clearPendingMandateInterval = () => {
    if (!pendingMandateIntervalRef.current) {
      return;
    }
    clearInterval(pendingMandateIntervalRef.current);
    pendingMandateIntervalRef.current = undefined;
  };

  const checkPendingMandate = async () => {
    if (!pendingMandateIbanId) {
      return;
    }

    const iban = await fetchIban(pendingMandateIbanId);
    if (!iban) {
      return;
    }

    if (!iban.mandateApprovedAt) {
      if (!hasAlreadyRequestedMandate && !hasRequestedMandateRef.current) {
        hasRequestedMandateRef.current = true;
        try {
          await requestMandateApproval(pendingMandateIbanId);
        } catch (unknownError) {
          clearPendingMandateInterval();
          setIsPendingMandateResolved(true);
          navigate(location.pathname, { replace: true });
          enqueueSnackbar({
            message: getLocalizedText("general.error"),
            variant: "error",
          });
        }
        return;
      }

      // Waiting on MangoPay's approval webhook to land after the redirect back.
      if (!pendingMandateIntervalRef.current) {
        pendingMandateIntervalRef.current = setInterval(
          checkPendingMandate,
          1000,
        );
      }
      return;
    }

    if (contractGroupsRef.current.length === 0) {
      // contractGroups is still loading - keep polling until we can
      // pre-select the account for every contract group.
      if (!pendingMandateIntervalRef.current) {
        pendingMandateIntervalRef.current = setInterval(
          checkPendingMandate,
          1000,
        );
      }
      return;
    }

    clearPendingMandateInterval();
    // `iban` here comes from fetchIban's direct API call, not from the redux
    // `ibans` store - refresh that store too, otherwise handleSave still sees
    // the stale, not-yet-approved account and re-requests a mandate instead
    // of calling updateMandate.
    if (user?.personalContactId) {
      dispatch(
        bankAccountActions.getContactBankAccountsStart.getAction({
          contactId: user.personalContactId,
        }),
      );
    }
    setSelectedIbanIdByContract(current => {
      const next = { ...current };
      contractGroupsRef.current.forEach(group => {
        next[group.contractId] = pendingMandateIbanId;
      });
      return next;
    });
    setIsPendingMandateResolved(true);
    navigate(location.pathname, { replace: true });
  };

  React.useEffect(() => {
    if (!pendingMandateIbanId) {
      setIsPendingMandateResolved(true);
      return;
    }

    checkPendingMandate();
    return () => clearPendingMandateInterval();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSelectedIbanId = (group: IContractMandateGroup) =>
    selectedIbanIdByContract[group.contractId] ?? group.activeBankAccountId;

  const handleSave = async (group: IContractMandateGroup) => {
    const selectedIbanId = getSelectedIbanId(group);

    if (selectedIbanId === NO_CONTINUOUS_VALUE) {
      await submitMandateUpdate(group, null);
      return;
    }

    const selectedIban = ibans?.find(iban => iban.id === selectedIbanId);

    if (!selectedIban?.mandateApprovedAt) {
      setSavingContractId(group.contractId);
      try {
        await requestMandateApproval(selectedIbanId);
      } catch (unknownError) {
        setSavingContractId(undefined);
        revertSelectedIbanId(group);
        enqueueSnackbar({
          message: getLocalizedText("general.error"),
          variant: "error",
        });
      }
      return;
    }

    await submitMandateUpdate(group, selectedIbanId);
  };

  // The blocks only appear once every contract is known, so they show up in
  // their final order and no Save button moves while the page loads.
  const isLoading =
    isFetchingIbans ||
    isFetchingContractGroups ||
    isResolvingContracts ||
    !isPendingMandateResolved;

  const getRadioOptions = (group: IContractMandateGroup) => {
    const selectedIbanId = getSelectedIbanId(group);

    // Only mandate-approved accounts are selectable here - an account
    // without an approved mandate can't actually be switched to yet (see
    // requestMandateApproval, which is what gets it approved in the first
    // place).
    const approvedIbans = (ibans ?? []).filter(acc => acc.mandateApprovedAt);

    return [
      ...approvedIbans.map(acc => ({
        label: (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            flex={1}
          >
            <Stack gap={0.5} minWidth={0}>
              <Typography sx={sx.content.optionTitle}>{acc.iban}</Typography>
              {acc.ownerName && (
                <Typography sx={sx.content.optionSubtitle}>
                  {acc.ownerName}
                </Typography>
              )}
            </Stack>
            {selectedIbanId === acc.id ? (
              <Box sx={sx.content.activeBadge}>
                {getLocalizedText("tenant.payments.direct_debit.badge_active")}
              </Box>
            ) : (
              <Box sx={sx.content.notUsedBadge}>
                {getLocalizedText(
                  "tenant.payments.direct_debit.badge_not_used",
                )}
              </Box>
            )}
          </Stack>
        ),
        value: acc.id,
      })),
      {
        label: (
          <Stack gap={0.5}>
            <Typography sx={sx.content.optionTitle}>
              {getLocalizedText(
                "tenant.payments.direct_debit.no_continuous_title",
              )}
            </Typography>
            <Typography sx={sx.content.optionSubtitle}>
              {getLocalizedText(
                "tenant.payments.direct_debit.no_continuous_description",
              )}
            </Typography>
          </Stack>
        ),
        value: NO_CONTINUOUS_VALUE,
      },
    ];
  };

  const renderContent = () => {
    if (isLoading) {
      return <SpinningLoader />;
    }

    // Save must stay out of reach while the accounts are unknown. With an empty
    // list the selected account resolves to undefined, which reads as "no mandate
    // approved yet" and sends the tenant off to Mangopay to re-sign a mandate
    // that was already approved.
    if (ibansFetchError) {
      return (
        <Typography variant="body1">
          {getLocalizedText("fetch.error")}
        </Typography>
      );
    }

    return (
      <Stack direction="column" gap={4}>
        {contractGroups.map(group => (
          <ContractMandateGroupBlock
            key={group.contractId}
            group={group}
            contract={contractsById[group.contractId]}
            selectedIbanId={getSelectedIbanId(group)}
            radioOptions={getRadioOptions(group)}
            isSaving={savingContractId === group.contractId}
            hasUnsavedChange={
              getSelectedIbanId(group) !== group.activeBankAccountId
            }
            onChangeSelectedIbanId={id =>
              setSelectedIbanIdByContract(current => ({
                ...current,
                [group.contractId]: id,
              }))
            }
            onSave={() => handleSave(group)}
          />
        ))}
        <Box sx={sx.content.addLink}>
          <Link
            label={`+ ${getLocalizedText(
              "payment_bank_account_list.cta.add_bank_account",
            )}`}
            underlined={false}
            onClick={() =>
              navigate(`${DIRECT_DEBITS_ROUTE}/add-bank-account`, {
                state: {
                  referrer: DIRECT_DEBITS_ROUTE,
                  infoMessage: getLocalizedText(
                    "tenant.payments.direct_debit.add_bank_mandate_notice",
                  ),
                },
              })
            }
          />
        </Box>
      </Stack>
    );
  };

  return (
    <Box>
      <Link
        label={`< ${getLocalizedText("payments.overview.title")}`}
        underlined={false}
        onClick={() => navigate("/payments")}
      />
      <Box mt={2}>
        <MainCard border content={false} sx={sx.wrapper}>
          <Box sx={sx.content.header}>
            <Typography variant="h4" sx={sx.content.title}>
              {getLocalizedText("tenant.payments.direct_debit.manage_title")}
            </Typography>
          </Box>
          <Divider />
          <Box sx={sx.content.body}>
            <Typography sx={sx.content.helperText}>
              {getLocalizedText("tenant.payments.direct_debit.manage_subtitle")}
            </Typography>
            {renderContent()}
          </Box>
        </MainCard>
      </Box>
    </Box>
  );
};
